import { ContractsExpandable } from '@dealstack/frontend/shared/components/ContractsExpandable/ContractsExpandable';
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Option, Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { DeleteButtonSquare } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ModalLayer } from '@dealstack/frontend/shared/components/fundament/Modal/ModalLayer';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Popover } from '@dealstack/frontend/shared/components/Popover/Popover';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { HeaderCell, Cell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	HeaderRow,
	DefaultTable,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import {
	useTranslationCommonUi,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ContractType, DataCounterpartyTct, SignatureType } from '@dealstack/shared/lib/generated';
import {
	isReviewSignatureType,
	isWetInkSignatureType,
} from '@dealstack/shared/lib/common/signatures';
import { Checkbox, Text as TextMantine, Tooltip as TooltipMantine } from '@mantine/core';
import { get } from 'lodash';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { NewJoinersAndAllocationsContractCounterpartiesModal } from './NewJoinersAndAllocationsContractCounterpartiesModal';
import { NewJoinersAndAllocationsContractInstructionsModal as NewJoinersAndAllocationsInvestorInstructionsModal } from './NewJoinersAndAllocationsContractInstructionsModal';
import { useNewJoinersAndAllocationsContractsController } from './useNewJoinersAndAllocationsContractsController';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';

interface Props {
	className?: string;
}

const NewJoinersAndAllocationsContracts = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const tCommonUi = useTranslationCommonUi();

	const {
		contractForm,
		showInvestorsModal,
		setShowInvestorsModal,
		currentRowIndex,
		onInstructionLocalSave,
		onInstructionLocalCancel,
		showCounterpartiesModal,
		setShowCounterpartiesModal,
		onCounterpartiesLocalSave,
		visibleContracts,
		contractFields,
		openCounterpartiesModal,
		openInvestorModal,
		onRemoveRowClicked,
		onUploadFileLocally,
		onDownloadDocumentTemplate,
		onDownloadContractInstruction,
	} = useNewJoinersAndAllocationsContractsController();

	return (
		<div className={className}>
			<ModalLayer>
				<SectionHeader size="h5b" title={tMipCreate('contracts.header.title')} />

				<FormProvider {...contractForm}>
					{showInvestorsModal ? (
						<NewJoinersAndAllocationsInvestorInstructionsModal
							onClose={() => setShowInvestorsModal(false)}
							rowIndex={currentRowIndex}
							onSave={onInstructionLocalSave}
							onCancel={onInstructionLocalCancel}
						/>
					) : null}
					{showCounterpartiesModal ? (
						<NewJoinersAndAllocationsContractCounterpartiesModal
							show={showCounterpartiesModal}
							onClose={() => setShowCounterpartiesModal(false)}
							rowIndex={currentRowIndex}
							onSave={onCounterpartiesLocalSave}
						/>
					) : null}

					<ContractsExpandable />
					<SectionDocuments>
						<DocumentsHeader
							size="h5b"
							title={tMipCreate('contracts.documents.title')}
						/>

						<form>
							<DefaultTable maxSize>
								<HeaderRow>
									<HeaderCell size={0.5}>#</HeaderCell>
									<HeaderCell customSize="1fr">
										{tMipCreate('contracts.table.headers.filename')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.contract_name'
										)}
									>
										{tMipCreate('contracts.table.headers.contract_name')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.investor_signature'
										)}
									>
										{tMipCreate('contracts.table.headers.investor_signature')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.contract_type'
										)}
									>
										{tMipCreate('contracts.table.headers.contract_type')}
									</HeaderCell>
									<HeaderCell
										childrenAlign="center"
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.witness'
										)}
									>
										{tMipCreate('contracts.table.headers.witness')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.counterparty_signature'
										)}
									>
										{tMipCreate('contracts.table.headers.counterparties')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.instruction_to_investors'
										)}
									>
										{tMipCreate('contracts.table.headers.instructions')}
									</HeaderCell>
									<HeaderCell size={2} />
								</HeaderRow>
								<BodyRow>
									{visibleContracts?.map((item, index) => {
										return (
											<Row key={`row_${item.id}`}>
												<Cell>
													<Text bold variant="p-sm">
														{`${index + 1}.`}
													</Text>
												</Cell>
												<Cell>
													<Text bold variant="p-sm" elipsis>
														{item?.file.name === 'n/a.pdf'
															? 'n/a'
															: item?.file.name}
													</Text>
												</Cell>
												<Cell>
													<TooltipMantine label={item.name}>
														<TextMantine
															size="B6"
															truncate={true}
															lineClamp={1}
															style={{ wordBreak: 'break-all' }}
														>
															{item.name}
														</TextMantine>
													</TooltipMantine>
												</Cell>
												<Cell>
													<Text variant="p-sm" elipsis>
														{(() => {
															switch (item.signatureType) {
																case SignatureType.ELECTRONIC:
																	return tMipCreate(
																		'contracts.table.signature_types.electronic'
																	);
																case SignatureType.REVIEW:
																	return tMipCreate(
																		'contracts.table.signature_types.review'
																	);
																default:
																	return tMipCreate(
																		'contracts.table.signature_types.wet_ink'
																	);
															}
														})()}
													</Text>
												</Cell>
												<Cell>
													<Text variant="p-sm" elipsis>
														{(() => {
															switch (item.type) {
																case ContractType.DEAL:
																	return tMipCreate(
																		'contracts.table.contract_types.deal'
																	);
																case ContractType.MULTISIGNATURE:
																	return tMipCreate(
																		'contracts.table.contract_types.multisignature'
																	);
																case ContractType.FORM:
																	return tMipCreate(
																		'contracts.table.contract_types.form'
																	);
																default:
																	return tMipCreate(
																		'contracts.table.contract_types.side_letter'
																	);
															}
														})()}
													</Text>
												</Cell>
												<Cell align="center">
													{!!item?.requiredWitnesses && (
														<Checkbox
															checked={!!item?.requiredWitnesses}
															disabled
														/>
													)}
												</Cell>
												<Cell>
													{item?.counterparties?.length ? (
														<Popover
															content={
																<CounterpartiesPopover
																	counterparties={
																		item?.counterparties
																	}
																/>
															}
														>
															<ChipContract
																prefixIcon="checkmark-circle-contained-filled"
																chipTheme={CHIP_THEME.LIGHT}
																color={COLOR.GREEN}
																size="s"
															>
																{item?.counterparties?.length > 1
																	? item?.counterparties?.length?.toString()
																	: ''}
															</ChipContract>
														</Popover>
													) : null}
												</Cell>
												<Cell>
													{item.instruction ? (
														<InstructionWrapper>
															<ChipContract
																prefixIcon="checkmark-circle-contained-filled"
																chipTheme={CHIP_THEME.LIGHT}
																color={COLOR.GREEN}
																size="s"
															/>

															<DownloadIcon
																onClick={() =>
																	onDownloadContractInstruction(
																		item
																	)
																}
																variant="download"
																className="clickAnimation"
															/>
														</InstructionWrapper>
													) : null}
												</Cell>
												<Cell>
													<Button
														variant="outlined"
														iconPlacement="left"
														icon="download"
														iconSize="lg"
														onClick={() => {
															onDownloadDocumentTemplate(
																item.file.id,
																item.name
															);
														}}
													>
														{tCommonUi('download.download')}
													</Button>
												</Cell>
											</Row>
										);
									})}
									{contractFields?.length
										? contractFields?.map((item, index) => {
												const fixedIndex =
													index + (visibleContracts?.length ?? 0);
												const numberOfCounterparties =
													item.counterparties?.length ?? 0;
												const itemFormName = `contracts.${index}`;
												const {
													signatureType: contractSignatureType,
													type,
												} = contractForm.getValues(`contracts.${index}`);

												const isMultisignature =
													type === ContractType.MULTISIGNATURE;
												const isReviewOnly =
													isReviewSignatureType(contractSignatureType);

												const { errors } = contractForm.formState;
												const isInvalid = !!get(
													errors,
													`contracts.${index}.name`
												);

												const isForm = type === ContractType.FORM;
												const isWetInk =
													isWetInkSignatureType(contractSignatureType);

												const isFormDisabled = !isWetInk;
												return (
													<RowWrapper
														key={`row_${fixedIndex}`}
														isInvalid={isInvalid}
													>
														<Row>
															<Cell>
																<Text bold variant="p-sm">
																	{`${fixedIndex + 1}.`}
																</Text>
															</Cell>
															<Cell>
																<Text bold variant="p-sm" elipsis>
																	{item?.fileInstance?.name}
																</Text>
															</Cell>
															<Cell>
																<UIElementIdentifier
																	identifier={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_CONTRACT_NAME_INPUT
																	}
																	index={index}
																>
																	<ContractNameInput
																		name={`${itemFormName}.name`}
																	/>
																</UIElementIdentifier>
															</Cell>
															<Cell>
																<UIElementIdentifier
																	identifier={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_INVESTOR_SIGNATURE_SELECT
																	}
																	index={index}
																>
																	<Select
																		name={`${itemFormName}.signatureType`}
																	>
																		<Option
																			value={
																				SignatureType.ELECTRONIC
																			}
																			disabled={isForm}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.electronic'
																			)}
																		</Option>
																		<Option
																			value={
																				SignatureType.WET_INK
																			}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.wet_ink'
																			)}
																		</Option>
																		<Option
																			value={
																				SignatureType.REVIEW
																			}
																			disabled={
																				isMultisignature ||
																				isForm
																			}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.review'
																			)}
																		</Option>
																	</Select>
																</UIElementIdentifier>
															</Cell>
															<Cell>
																<UIElementIdentifier
																	identifier={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_CONTRACT_TYPE_SELECT
																	}
																	index={index}
																>
																	<Select
																		name={`${itemFormName}.type`}
																	>
																		<Option
																			value={
																				ContractType.DEAL
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.deal'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.MULTISIGNATURE
																			}
																			disabled={isReviewOnly}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.multisignature'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.SIDE_LETTER
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.side_letter'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.FORM
																			}
																			disabled={
																				isFormDisabled ||
																				!!numberOfCounterparties
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.form'
																			)}
																		</Option>
																	</Select>
																</UIElementIdentifier>
															</Cell>
															<Cell align="center">
																<FormCheckbox
																	name={`${itemFormName}.hasWitnesses`}
																	disabled={
																		isReviewOnly || isForm
																	}
																/>
															</Cell>
															<Cell>
																{numberOfCounterparties &&
																!isReviewOnly ? (
																	<EditWrapper>
																		<ChipContract
																			prefixIcon="checkmark-circle-contained-filled"
																			chipTheme={
																				CHIP_THEME.LIGHT
																			}
																			color={COLOR.GREEN}
																			size="s"
																		>
																			{/* display numbers near checkmark if there is more than one counterparty*/}
																			{numberOfCounterparties >
																			1
																				? numberOfCounterparties.toString()
																				: ''}
																		</ChipContract>
																		<UIElementIdentifier
																			identifier={
																				TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_EDIT_COUNTERPARTY_BUTTON
																			}
																			index={index}
																		>
																			<EditButton
																				onClick={() =>
																					openCounterpartiesModal(
																						item.id
																					)
																				}
																			/>
																		</UIElementIdentifier>
																	</EditWrapper>
																) : (
																	<UIElementIdentifier
																		identifier={
																			TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_ADD_COUNTERPARTY_BUTTON
																		}
																		index={index}
																	>
																		<AddButton
																			disabled={
																				isReviewOnly ||
																				isForm
																			}
																			onClick={() =>
																				openCounterpartiesModal(
																					item.id
																				)
																			}
																		/>
																	</UIElementIdentifier>
																)}
															</Cell>
															<Cell>
																{item.instruction?.length ? (
																	<EditWrapper>
																		<ChipContract
																			prefixIcon="checkmark-circle-contained-filled"
																			chipTheme={
																				CHIP_THEME.LIGHT
																			}
																			color={COLOR.GREEN}
																			size="s"
																		/>
																		<UIElementIdentifier
																			identifier={
																				TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_EDIT_INSTRUCTION_BUTTON
																			}
																			index={index}
																		>
																			<EditButton
																				onClick={() =>
																					openInvestorModal(
																						item.id
																					)
																				}
																			/>
																		</UIElementIdentifier>
																	</EditWrapper>
																) : (
																	<UIElementIdentifier
																		identifier={
																			TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_ADD_INSTRUCTION_BUTTON
																		}
																		index={index}
																	>
																		<AddButton
																			onClick={() =>
																				openInvestorModal(
																					item.id
																				)
																			}
																		/>
																	</UIElementIdentifier>
																)}
															</Cell>
															<Cell>
																<UIElementIdentifier
																	identifier={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_DELETE_CONTRACT_BUTTON
																	}
																	index={index}
																>
																	<DeleteButtonSquare
																		onClick={() => {
																			onRemoveRowClicked(
																				item.id
																			);
																		}}
																	/>
																</UIElementIdentifier>
															</Cell>
														</Row>
													</RowWrapper>
												);
											})
										: null}
								</BodyRow>
							</DefaultTable>
						</form>

						<DragAndDropWrapper
							onDrop={onUploadFileLocally}
							accept={{ 'application/pdf': ['.pdf'] }}
							invalidTypeMessage={tMipCreate(
								'contracts.table.errors.invalid_file_type'
							)}
						/>
					</SectionDocuments>
				</FormProvider>
			</ModalLayer>
		</div>
	);
};

const CounterpartiesPopover = styled(
	({
		className,
		counterparties,
	}: {
		className?: string;
		counterparties: DataCounterpartyTct[];
	}) => {
		const tMipCreate = useTranslationMipCreate();

		return (
			<div className={className}>
				{counterparties.map((item) => (
					<div key={item.name}>
						<Text variant="p-md">{item?.name}</Text>
						<Text variant="p-md">
							{(() => {
								if (item.type === 'ONE_SIGNATORIES') {
									return tMipCreate(
										'contracts.modals.counterparties.one_signatories'
									);
								}
								return tMipCreate(
									'contracts.modals.counterparties.two_signatories'
								);
							})()}
						</Text>
					</div>
				))}
			</div>
		);
	}
)`
	& > div {
		&:not(:first-of-type) {
			margin-top: 0.5em;
		}

		span:first-of-type {
			margin-right: 1em;
		}
	}
`;

const InstructionWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.7em;
`;

const DownloadIcon = styled(Icon)`
	cursor: pointer;
`;

const ChipContract = styled(ChipsV2)`
	height: 1.25rem;
	padding: 0.125em 0.75em;
`;
const AddButton = styled(
	({
		className,
		onClick,
		disabled,
	}: {
		className?: string;
		onClick: () => void;
		disabled?: boolean;
	}) => {
		const tMipCreate = useTranslationMipCreate();
		return (
			<Button
				onClick={onClick}
				className={`${className} clickAnimation`}
				iconSize="lg"
				icon="add-circle"
				iconPlacement="left"
				size="medium"
				variant="text"
				disabled={disabled}
			>
				{tMipCreate('contracts.table.add')}
			</Button>
		);
	}
)``;
const EditWrapper = styled.div`
	display: flex;
	flex-direction: row;
	padding: 0 0.313em;
	justify-content: center;
	align-items: center;
`;

const EditButton = styled(({ className, onClick }: { className?: string; onClick: () => void }) => {
	const tMipCreate = useTranslationMipCreate();

	return (
		<Button
			onClick={onClick}
			className={`${className} clickAnimation`}
			iconSize="lg"
			icon="edit-pencil"
			iconPlacement="left"
			size="medium"
			variant="text"
		>
			{tMipCreate('contracts.table.edit')}
		</Button>
	);
})`
	padding: 0.75em;
`;

const SectionDocuments = styled.div``;
const DragAndDropWrapper = styled(DragAndDrop)``;
const DocumentsHeader = styled(SectionHeader)`
	margin-top: 2em;
`;

const ContractNameInput = styled(StringInput)``;

const Memoized = memo(NewJoinersAndAllocationsContracts);
const Styled = styled(Memoized)`
	${DeleteButtonSquare} {
		margin-left: 2em;
	}

	${DragAndDropWrapper} {
		margin-top: 4.75em;
	}

	${DefaultTable} {
		overflow: unset;
	}

	${HeaderRow} {
		align-items: center;
	}

	${FormCheckbox} {
		height: 2.18rem;
		input:not(:checked) {
			border: 1px solid ${defaultTheme.v3.colors.gray[4]};
		}
	}
`;

export { Styled as NewJoinersAndAllocationsContracts };
